import { safeLocalStorage } from '@toss/storage';

const FALLBACK_FP_STORAGE_KEY = 'zep.quiz.ai.fingerprint-id';

let fingerprintPromise: Promise<string | null> | null = null;
let memoryFallbackFingerprintId: string | null = null;

function createFallbackFingerprintId() {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `fallback-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getStoredFallbackFingerprintId(): string | null {
  try {
    return safeLocalStorage.get(FALLBACK_FP_STORAGE_KEY);
  } catch {
    return null;
  }
}

function saveFallbackFingerprintId(value: string): void {
  try {
    safeLocalStorage.set(FALLBACK_FP_STORAGE_KEY, value);
  } catch {
    // In restricted browser contexts, an in-memory fallback still keeps the
    // current page session identifiable for quota requests.
  }
}

function getFallbackFingerprintId(): string {
  if (!memoryFallbackFingerprintId) {
    memoryFallbackFingerprintId = createFallbackFingerprintId();
  }

  saveFallbackFingerprintId(memoryFallbackFingerprintId);
  return memoryFallbackFingerprintId;
}

async function getFingerprintIdFromLibrary() {
  if (typeof window === 'undefined') return null;

  try {
    const { default: FingerprintJS } = await import(
      '@fingerprintjs/fingerprintjs'
    );
    const fpAgent = await FingerprintJS.load();
    const fpResult = await fpAgent.get();
    return fpResult.visitorId ?? null;
  } catch {
    return null;
  }
}

export async function getClientFingerprintId() {
  if (typeof window === 'undefined') return null;

  const storedFallbackId = getStoredFallbackFingerprintId();
  if (storedFallbackId) return storedFallbackId;

  if (!fingerprintPromise) {
    fingerprintPromise = getFingerprintIdFromLibrary();
  }

  const libraryFpId = await fingerprintPromise;
  if (libraryFpId) {
    saveFallbackFingerprintId(libraryFpId);
    return libraryFpId;
  }

  return getFallbackFingerprintId();
}
